import React from 'react';
import { Modal, Button } from 'antd';
import guideImg from '../../images/gmail-consent-guide.webp';
import css from './GmailConsentPrimer.module.scss';

export type GmailConsentPrimerVariant = 'connect' | 'fix';

interface GmailConsentPrimerProps {
  open: boolean;
  onContinue: () => void;
  onCancel: () => void;
  variant?: GmailConsentPrimerVariant;
}

/**
 * Shown before every redirect to Google's Gmail consent screen. Google leaves
 * the "Send email on your behalf" checkbox UNCHECKED by default, and most
 * users miss it — which connects the account but strips the one permission we
 * need. This primer coaches the checkbox before the user leaves; the 'fix'
 * variant is the re-consent pass after a missed box.
 */
const GmailConsentPrimer: React.FC<GmailConsentPrimerProps> = ({
  open,
  onContinue,
  onCancel,
  variant = 'connect',
}) => (
  <Modal
    title={
      variant === 'fix'
        ? 'This time, check the box ✓'
        : "One checkbox Google won't check for you"
    }
    open={open}
    onCancel={onCancel}
    width="min(480px, 94vw)"
    className={css.primerModal}
    footer={[
      <Button key="cancel" onClick={onCancel}>
        Cancel
      </Button>,
      <Button key="continue" type="primary" onClick={onContinue}>
        Got it — continue to Google
      </Button>,
    ]}
  >
    <img
      src={guideImg}
      alt="Google consent screen with 'Send email on your behalf' checked"
      className={css.guideImage}
    />
    {variant === 'fix' ? (
      <p className={css.copy}>
        Google will show the same permission screen again. This time, make sure{' '}
        <strong>'Send email on your behalf'</strong> is checked — without it we
        can't send your applications.
      </p>
    ) : (
      <p className={css.copy}>
        On the next screen, make sure <strong>'Send email on your behalf'</strong>{' '}
        is checked — Google leaves it unchecked by default. Without it we can't
        send your applications.
      </p>
    )}
  </Modal>
);

export default GmailConsentPrimer;
